// src/lib/api.js
import axios from "axios";

const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
  xsrfCookieName: 'csrftoken', 
  xsrfHeaderName: 'X-CSRFToken',
});

function getStoredToken(key) {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key) ?? null;
}

function setStoredToken(key, value) {
  if (localStorage.getItem("refresh")) {
    localStorage.setItem(key, value);
  } else {
    sessionStorage.setItem(key, value);
  }
}

api.interceptors.request.use((config) => {
  const t = getStoredToken("access");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

let refreshing = false;
let waiters = [];

let retriedUrls = new Set(); 

async function refreshAccessToken() {
  const refresh = getStoredToken("refresh");
  if (!refresh) throw new Error("NO_REFRESH");
  

  const res = await axios.post("/api/auth/token/refresh/", { refresh });
  const { access } = res.data || {};
  if (!access) throw new Error("REFRESH_FAILED");
  setStoredToken("access", access);
  return access;
}

api.interceptors.response.use(
  (res) => {
  
    if (res.config && res.config.url) {
      retriedUrls.delete(res.config.url);
    }
    return res;
  },
  async (error) => {
    const { config, response } = error || {};
    
    if (!response) {
      return Promise.reject(error);
    }

    const urlString = String(config.url).toLowerCase();

    // リフレッシュ/トークン発行自体の失敗は認証切れ → ログインへ
    if (urlString.includes('refresh') || urlString.includes('token')) {
      clearAllTokens();
      redirectToLogin();
      return Promise.reject(error);
    }

    // 401 以外（403=権限不足 / 404 / 500 等）は「認証切れ」ではない。
    // トークンを消さず・リダイレクトせず、そのままエラーを返して各画面で処理させる。
    // ※ 以前はここで全エラーをログアウト扱いし、権限不足の勤務地マスタ等で /m/login へ飛んでいた。
    if (response.status !== 401) {
      return Promise.reject(error);
    }

    // 401 を一度リトライ済みなら、本当に認証切れとしてログインへ
    if (retriedUrls.has(config.url)) {
      retriedUrls.delete(config.url);
      clearAllTokens();
      redirectToLogin();
      return Promise.reject(error);
    }

    retriedUrls.add(config.url);

    try {
      if (refreshing) {
        await new Promise((r) => waiters.push(r));
      } else {
        refreshing = true;
        await refreshAccessToken();
        refreshing = false;
        waiters.forEach((r) => r());
        waiters = [];
      }

      const t = getStoredToken("access");
      if (t) {
        config.headers.Authorization = `Bearer ${t}`;
      }
      
   
      return axios(config); 

    } catch (e) {
      refreshing = false;
      waiters.forEach((r) => r());
      waiters = [];
      
      retriedUrls.delete(config.url);
      clearAllTokens();
      redirectToLogin();
      return Promise.reject(error);
    }
  }
);

function clearAllTokens() {
  ["access", "refresh"].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

function redirectToLogin() {
  if (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/dashboard')) {
    window.location.href = '/admin/login';
  } else {
    window.location.href = '/m/login';
  }
}

export default api;